import React, { ReactNode, useRef, createContext, useContext } from "react"

import { Notifier } from "@airbrake/browser"
import { INoticeError } from "@airbrake/browser/dist/notice"

const AIRBRAKE_PROJECT_ID = import.meta.env.REACT_APP_AIRBRAKE_PROJECT_ID
const AIRBRAKE_API_KEY = import.meta.env.REACT_APP_AIRBRAKE_API_KEY || ""

// Filter out hubspot InvalidCharacterErrors
const shouldFilterOutError = (error: INoticeError): boolean => {
  const files = error.backtrace?.map((b) => b.file) || []

  const isHubspotScript = files.some((file) =>
    file?.includes("js.hs-analytics.net"),
  )
  const isInvalidCharacterError = error.type === "InvalidCharacterError"

  return isHubspotScript && isInvalidCharacterError
}

interface AirbrakeContextType {
  notifier: Notifier | null
}

const AirbrakeContext = createContext<AirbrakeContextType>({
  notifier: null as Notifier | null,
})

export const useAirbrake = (): AirbrakeContextType =>
  useContext(AirbrakeContext)

const AirbrakeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}: {
  children: ReactNode
}) => {
  const airbrakeRef = useRef<Notifier | null>(null)

  if (!airbrakeRef.current && AIRBRAKE_PROJECT_ID && AIRBRAKE_API_KEY) {
    airbrakeRef.current = new Notifier({
      projectId: AIRBRAKE_PROJECT_ID,
      projectKey: AIRBRAKE_API_KEY,
      environment: import.meta.env.REACT_APP_ENV,
    })
    airbrakeRef.current.addFilter((notice) => {
      const error = notice.errors?.[0]
      if (error && shouldFilterOutError(error)) {
        return null
      }
      return notice
    })
  }

  return (
    <AirbrakeContext.Provider value={{ notifier: airbrakeRef.current }}>
      {children}
    </AirbrakeContext.Provider>
  )
}

export default AirbrakeProvider
